import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import CellTooltip from '../../../components/CellTooltip'
import { fetchToppliste } from './api'
import type { TopplistePlass, TopplisteRad, TopplisteResponse } from './api'
import { comparisonRange } from './periodeComparison'
import type { ComparisonMode } from './periodeComparison'

/**
 * Mest solgte (#928 AC4) and Største endringer (AC5) for the selected period,
 * against the comparison the graph uses. Movers need 30 sales in both periods.
 */

interface Props {
  from: string
  to: string
  klasse: string[]
  varebil: boolean | undefined
  omregistrering: boolean
  selgerPostnummer: string | null
  kjoperPostnummer: string | null
  mode: ComparisonMode
}

const fmtDate = (iso: string) => iso.split('-').reverse().join('.')
const fmtInt = (n: number) => n.toLocaleString('nb-NO')
const fmtPct = (n: number) =>
  `${n > 0 ? '+' : ''}${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
const fmtSigned = (n: number) => `${n > 0 ? '+' : ''}${fmtInt(n)}`
const changeColor = (n: number | null) =>
  n === null || n === 0 ? '' : n > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'
const name = (r: TopplisteRad) => (r.modell ? `${r.merke} ${r.modell}` : r.merke ?? '')

// Dotted underline: the header has a hover tooltip.
function Tip({ label, tip }: { label: string; tip: string }) {
  return (
    <CellTooltip content={tip} width={280}>
      <span className="cursor-help underline decoration-dotted underline-offset-2">{label}</span>
    </CellTooltip>
  )
}

function Tabell({ title, rows, testId, firstCol, prevRange }: {
  title: string; rows: TopplistePlass[]; testId: string; firstCol: string; prevRange: string | null
}) {
  const { t } = useTranslation('bruktbil')
  const dash = <span title={t('oversikt.topp.ingenSammenligning')}>–</span>
  return (
    <div className="card p-4 overflow-x-auto" data-testid={testId}>
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.ingenData')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
              <th className="py-1 pr-2 font-medium">{t('common.rang')}</th>
              <th className="py-1 pr-2 font-medium">{firstCol}</th>
              <th className="py-1 pr-2 font-medium text-right">{t('common.antall')}</th>
              <th className="py-1 pr-2 font-medium text-right">
                <Tip label={t('oversikt.topp.antallForrige')}
                  tip={prevRange ? t('oversikt.topp.tooltip.antallForrige', { range: prevRange }) : t('oversikt.topp.ingenSammenligning')} />
              </th>
              <th className="py-1 pr-2 font-medium text-right">{t('oversikt.topp.endring')}</th>
              <th className="py-1 pr-2 font-medium text-right">
                <Tip label={t('oversikt.topp.endringPct')} tip={t('oversikt.topp.tooltip.endringPct')} />
              </th>
              <th className="py-1 font-medium text-right">
                <Tip label={t('common.andelPct')} tip={t('oversikt.topp.tooltip.andelPct')} />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.rang} className="border-t border-gray-100 dark:border-dark-border text-gray-800 dark:text-gray-200">
                <td className="py-1 pr-2 text-gray-500 dark:text-gray-400">{r.rang}</td>
                <td className="py-1 pr-2">{name(r)}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{fmtInt(r.antall)}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{r.antall_forrige === null ? dash : fmtInt(r.antall_forrige)}</td>
                <td className={`py-1 pr-2 text-right tabular-nums ${changeColor(r.endring)}`}>
                  {r.endring === null ? dash : fmtSigned(r.endring)}
                </td>
                <td className={`py-1 pr-2 text-right tabular-nums ${changeColor(r.endring_pct)}`}>
                  {r.endring_pct === null ? dash : fmtPct(r.endring_pct)}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {r.andel_pct.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function Bevegelser({ title, rows, testId }: { title: string; rows: TopplisteRad[]; testId: string }) {
  const { t } = useTranslation('bruktbil')
  return (
    <div data-testid={testId}>
      <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('oversikt.topp.ingenBevegelser')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {rows.map(r => (
            <li key={name(r)} className="flex items-baseline justify-between gap-2 text-gray-800 dark:text-gray-200">
              <span>{name(r)}</span>
              <span className="whitespace-nowrap tabular-nums">
                <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                  {fmtInt(r.antall_forrige ?? 0)} → {fmtInt(r.antall)}
                </span>
                <span className={`font-medium ${changeColor(r.endring_pct)}`}>{r.endring_pct === null ? '–' : fmtPct(r.endring_pct)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default function Topplister({ from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer, mode }: Props) {
  const { t } = useTranslation('bruktbil')
  const [data, setData] = useState<TopplisteResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Totals, not buckets: week and month granularity give the same range.
  const prev = useMemo(() => comparisonRange(from, to, mode, 'month'), [from, to, mode])
  const klasseKey = klasse.join('|')

  useLatest(alive => {
    setLoading(true)
    setError(null)
    fetchToppliste(
      { from, to, klasse: klasseKey ? klasseKey.split('|') : [], varebil, omregistrering, selgerPostnummer, kjoperPostnummer },
      prev,
    )
      .then(r => { if (alive()) setData(r.data) })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : '') })
      .finally(() => { if (alive()) setLoading(false) })
  }, [from, to, klasseKey, varebil, omregistrering, selgerPostnummer, kjoperPostnummer, prev.from, prev.to])

  if (error !== null) {
    return (
      <div className="card p-4 text-sm text-red-600 dark:text-red-400">
        {t('oversikt.feilVedLasting', { error: error || t('oversikt.ukjentFeil') })}
      </div>
    )
  }
  if (!data) {
    return <div data-testid="topp-skeleton" className="card animate-pulse h-64 bg-gray-100 dark:bg-dark-border rounded-sm" />
  }

  const range = `${fmtDate(data.fra)}–${fmtDate(data.til)}`
  const prevRange = data.prev_fra && data.prev_til ? `${fmtDate(data.prev_fra)}–${fmtDate(data.prev_til)}` : null
  return (
    <section className={`space-y-4 ${loading ? 'opacity-50' : ''}`} data-testid="topplister">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('oversikt.topp.tittel')}</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="topp-perioder">
          {prevRange ? t('oversikt.topp.perioder', { range, prevRange }) : range}
        </p>
      </div>

      {data.comparison_unavailable === 'before_history' && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="topp-before-history">
          {t('oversikt.topp.forHistorikk')}
        </p>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Tabell title={t('oversikt.topp.modeller')} rows={data.modeller} testId="topp-modeller" firstCol={t('common.modell')} prevRange={prevRange} />
        <Tabell title={t('oversikt.topp.merker')} rows={data.merker} testId="topp-merker" firstCol={t('common.merke')} prevRange={prevRange} />
      </div>

      <div className="card p-4 space-y-3" data-testid="storste-endringer">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          <Tip label={t('oversikt.topp.endringerTittel')} tip={t('oversikt.topp.tooltip.endringer')} />
        </h3>
        {data.bevegelser ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Bevegelser title={t('oversikt.topp.opp')} rows={data.bevegelser.opp} testId="bevegelser-opp" />
            <Bevegelser title={t('oversikt.topp.ned')} rows={data.bevegelser.ned} testId="bevegelser-ned" />
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('oversikt.topp.ingenSammenligning')}</p>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('oversikt.topp.endringerMetode')}</p>
      </div>
    </section>
  )
}
